import React, { useEffect } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';

import SectionListings from './components/SectionListings/SectionListings';
import SectionMarketplaceHero from './components/SectionMarketplaceHero/SectionMarketplaceHero';
import SectionHomeWays from './components/SectionHomeWays/SectionHomeWays';
import SectionHomeMission from './components/SectionHomeMission/SectionHomeMission';
import SectionHomeSupply from './components/SectionHomeSupply/SectionHomeSupply';
import SectionHomeFooter from './components/SectionHomeFooter/SectionHomeFooter';
import MobileHomeDock from './components/MobileHomeDock/MobileHomeDock';

import css from './LandingPage.module.css';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const MOBILE_HOME_INTERCOM_PADDING = 84;

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;
  const intl = useIntl();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
      return undefined;
    }

    const mq = window.matchMedia('(max-width: 767px)');
    const syncIntercomPadding = () => {
      window.Intercom('update', {
        vertical_padding: mq.matches ? MOBILE_HOME_INTERCOM_PADDING : 0,
      });
    };

    syncIntercomPadding();
    mq.addEventListener('change', syncIntercomPadding);

    return () => {
      mq.removeEventListener('change', syncIntercomPadding);
      window.Intercom('update', { vertical_padding: 0 });
    };
  }, []);

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      layoutClassName={css.landingLayout}
      footerClassName={css.landingFooter}
      topbarCurrentPage="LandingPage"
      firstSection={
        <div className={css.mobileHome}>
          <SectionMarketplaceHero sectionId="hero-section" />

          <SectionListings
            sectionId="new-arrivals-section"
            autoRefresh
            title={intl.formatMessage({ id: 'LandingPage.mobileHome.listings.newArrivals' })}
            startFrom={0}
            seeAllSearch={{}}
            moreLabel={
              <FormattedMessage
                id="LandingPage.mobileHome.listings.newArrivalsMore"
                values={{ lineBreak: <br /> }}
              />
            }
          />

          <SectionListings
            title={intl.formatMessage({ id: 'LandingPage.mobileHome.listings.browsePopularProps' })}
            startFrom={0}
            filters={{ pub_popular: true }}
            seeAllSearch={{ pub_popular: true }}
            moreLabel={
              <FormattedMessage
                id="LandingPage.mobileHome.listings.browseAllPropsMore"
                values={{ lineBreak: <br /> }}
              />
            }
          />

          <SectionListings
            sectionId="recent-staff-picks-section"
            title={intl.formatMessage({ id: 'LandingPage.mobileHome.listings.recentStaffPicks' })}
            startFrom={0}
            filters={{ pub_staffPicks: true }}
            seeAllSearch={{ pub_staffPicks: true }}
            moreLabel={
              <FormattedMessage
                id="LandingPage.mobileHome.listings.staffPicksMore"
                values={{ lineBreak: <br /> }}
              />
            }
          />
          <SectionHomeWays />
          <SectionHomeMission />
          <SectionHomeSupply />
          <SectionHomeFooter />
          <MobileHomeDock />
        </div>
      }
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
